import { callExternalApi } from './external-api.service';
import { getLogger } from '../utils/logger';

const logger = getLogger('auth-tracking.service');

export interface AuthTrackingResponse {
  status: string;
  message: string;
}

/**
 * Track user login event
 * @param method - Login method (e.g., 'standard', 'social', 'signup')
 * @param token - JWT access token
 * @returns Promise resolving to tracking response
 */
export const trackLogin = async (method: string = 'standard', token: string) => {
  try {
    logger.debug('Tracking login event', { method });
    
    const config = {
      url: `${process.env.REACT_APP_API_SERVER_URL}/api/auth/login`,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
      data: {
        method: method,
      },
    };

    const response = await callExternalApi({ config });
    
    if (response.error) {
      logger.error('Failed to track login', response.error);
      return { success: false, error: response.error };
    }
    
    logger.debug('Login tracked successfully', response.data);
    return { success: true, data: response.data };
    
  } catch (error) {
    logger.error('Error tracking login', error);
    return { success: false, error: { message: String(error) } };
  }
};

/**
 * Track user logout event
 * @param token - JWT access token
 * @returns Promise resolving to tracking response
 */
export const trackLogout = async (token: string) => {
  try {
    logger.debug('Tracking logout event');
    
    const config = {
      url: `${process.env.REACT_APP_API_SERVER_URL}/api/auth/logout`,
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
      data: {},
    };

    const response = await callExternalApi({ config });
    
    if (response.error) {
      logger.error('Failed to track logout', response.error);
      return { success: false, error: response.error };
    }
    
    logger.debug('Logout tracked successfully', response.data);
    return { success: true, data: response.data };
    
  } catch (error) {
    logger.error('Error tracking logout', error);
    return { success: false, error: { message: String(error) } };
  }
};

/**
 * Hook to track authentication events
 * @returns Object with trackLogin and trackLogout functions
 */
export const useAuthTracking = () => {
  return {
    trackLogin,
    trackLogout,
  };
};